/** @jsxImportSource @emotion/react */

import { useContext, useEffect, useRef, useState } from 'react';
import { CSSObject } from '@emotion/react';
import BlockItem, { IBlockBaseItem, IBlockItem } from '../blockItem';

import { IAction } from '../../../../../hooks/useAction';
import useTheme from '../../../../../hooks/useTheme';
import { hexToRgb, hexToRgba } from '../../../../../utils/hexToRgba';
import Clickable from '../../../../../components/clickable';
import { ScreenContext } from '../../../../../contexts/screenContext';
import { IBlockTextItem } from './blockTextItem';
import { IBlockRadioButtonItem } from './blockRadioButtonItem';

export type IBlockContainerOrientation = 'vertical' | 'horizontal';

export interface IBlockStackContainerItem extends IBlockBaseItem {
  type: 'stack_container';

  items: Array<IBlockItem>;
  orientation: IBlockContainerOrientation;
  highlightColor?: string;
  scrollable?: {
    itemsPercentageOfParentWidth: number;
  };
  shadow?: {
    offset: number;
    color: string;
    opacity: number;
    radius: number;
  };

  action?: IAction;
}

interface IBlockStackContainerItemProps {
  data: IBlockStackContainerItem;
}

const BlockStackContainerItem = ({
  data,
  ...props
}: IBlockStackContainerItemProps) => {
  const { hiddenIds } = useContext(ScreenContext);
  const { resolveColor } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const {
    items,
    orientation,
    highlightColor,
    action,
    shadow,
    scrollable,
    padding,
  } = data;

  useEffect(() => {
    if (scrollable && orientation === 'horizontal' && containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [scrollable, orientation]);

  const containsRadioButtons = items.some(
    (item) => item.type === 'radio_button'
  );

  // The reason for processing block item to relate and group radio button with its label/text
  // today view-component does not support grouping of radio block button I cannot avoid manipulating the existing item
  const processedItems = items.reduce<Array<IBlockItem>>((acc, item, index) => {
    // If the previous item was a radio button, this text item has already been processed.
    if (item.type === 'text' && items[index - 1]?.type === 'radio_button') {
      return acc;
    }

    if (item.type === 'radio_button') {
      const nextItem = items[index + 1];
      if (nextItem?.type === 'text') {
        const textItem = nextItem as IBlockTextItem;
        acc.push({
          ...item,
          textLegend: textItem.text,
        });
        return acc;
      }
    }

    acc.push(item);
    return acc;
  }, []);

  // Check whether the container has input items that do not handle
  // their own focus state. In those cases, the entire container is rendered
  // as a label, is clickable and renders a focus border.
  const hasNonFocusableInputItem = () => {
    return !!processedItems.find((item) => {
      switch (item.type) {
        case 'checkbox':
        case 'radio_button':
        case 'toggle':
          return true;
        default:
          return false;
      }
    });
  };

  const getRadioGroupLabelId = () => {
    if (!containsRadioButtons) return undefined;
    const firstRadio = processedItems.find(
      (item) => item.type === 'radio_button'
    ) as IBlockRadioButtonItem | undefined;
    return firstRadio?.inputAccessibility?.legendIdentifier;
  };

  // A crude way of making accordions more accessible.
  // If the container has a "modify" action with a toggles array, assume it's an accordion.
  // Cross-reference the first part id in the array with the current state of screen hiddenIds array
  // to guesstimate whether the accordion is expanded or collapsed.
  const getPotentialAccordionState = () => {
    if (action?.type === 'modify' && action.data.toggles?.length > 0) {
      return !hiddenIds.includes(action.data.toggles[0].id);
    }
  };

  const resolvedHighlightColor = highlightColor && resolveColor(highlightColor);

  const resolvedHighlightToRgb =
    (resolvedHighlightColor && hexToRgba(resolvedHighlightColor)?.join(',')) ||
    null;

  const CONTAINER_STYLE: CSSObject = {
    display: 'flex',
    boxSizing: 'border-box',

    flexDirection: orientation === 'horizontal' ? 'row' : 'column',

    ...(shadow && {
      boxShadow: `0px ${shadow.offset}px
      ${2 * shadow.radius}px
      rgba(${hexToRgb(resolveColor(shadow.color) || '')},${shadow.opacity})`,
    }),

    ...(highlightColor && {
      // isolation: isolate creates a stacking context so that the ::after overlay
      // with z-index: -1 renders above the background but below the children (text, icons).
      isolation: 'isolate',
      // The reason for this is that the designer wants an abstract layer on top of the block button to show the highlight color with opacity,
      // instead of applying opacity directly to the button
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        backgroundColor: 'transparent',
        pointerEvents: 'none',
      },

      '&[active]::after': {
        backgroundColor: `rgba(${resolvedHighlightToRgb})`,
      },
    }),
    ...(scrollable !== undefined && orientation === 'horizontal'
      ? {
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',

          // Inject rule for children
          // All children must not shrink by default
          '& > *': {
            flexShrink: 0,
          },
          // Stretch item specialization
          '& > .block-item-stretch': containerWidth
            ? {
                flexBasis: `${
                  scrollable.itemsPercentageOfParentWidth *
                  (containerWidth - padding.leading - padding.trailing)
                }px`,
                flexGrow: 0,
              }
            : undefined,
        }
      : {
          overflow: 'hidden',
        }),
  };

  return (
    <Clickable
      renderAs={hasNonFocusableInputItem() ? 'label' : undefined}
      defaultHandler={hasNonFocusableInputItem()}
      action={!hasNonFocusableInputItem() ? action : undefined}
      {...props}
      css={CONTAINER_STYLE}
      aria-labelledby={getRadioGroupLabelId()}
      aria-expanded={getPotentialAccordionState()}
      ref={containerRef}
    >
      {processedItems.map((item, index) => {
        const isStretch =
          item.horizontalLayout.type === 'stretch' &&
          scrollable?.itemsPercentageOfParentWidth !== undefined;

        const className = isStretch ? 'block-item-stretch' : undefined;

        return (
          <BlockItem
            key={index}
            {...item}
            parentOrientation={orientation}
            {...(className ? { className: 'block-item-stretch' } : {})}
          />
        );
      })}
    </Clickable>
  );
};

export default BlockStackContainerItem;
